import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Breadcrumbs } from "../AdminLayout";
import { seoApi } from "./api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Search, CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";

const COLL_TO_TYPE_ROUTE = {
  articles: "articles", conditions: "conditions", faqs: "faqs", guidelines: "guidelines",
  research_papers: "research-papers", research: "research-updates",
  evidence_summaries: "evidence-summaries",
  videos: "videos", patient_guides: "patient-guides", infographics: "infographics",
  books: "books", testimonials: "testimonials", media_appearances: "media-appearances",
};

const statusIcon = {
  pass: <CheckCircle2 size={13} className="text-[#3F6250]"/>,
  warn: <AlertTriangle size={13} className="text-[#D97706]"/>,
  fail: <XCircle size={13} className="text-[#9F1239]"/>,
  info: <Info size={13} className="text-stone2"/>,
};

export default function SeoDashboard() {
  const [dash, setDash] = useState(null);
  const [selected, setSelected] = useState(null);   // { coll, id }
  const [audit, setAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  useEffect(() => {
    seoApi.dashboard().then(setDash).catch(e => toast.error(formatErr(e.response?.data?.detail)));
  }, []);

  const runAudit = async (coll, id) => {
    setSelected({ coll, id });
    setLoadingAudit(true); setAudit(null);
    try { const a = await seoApi.audit(coll, id); setAudit(a); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoadingAudit(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="seo-dashboard">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Site" }, { label: "SEO & Structured Data" }]}/>
      <div className="font-serif text-3xl text-navy mt-4 mb-6 flex items-center gap-3"><Search size={22}/> SEO & Structured Data Validation</div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5">
          <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-3">Content types</div>
          {!dash ? <div className="text-xs text-stone2">Loading…</div> : (
            <div className="space-y-2" data-testid="seo-type-list">
              {dash.types.map(t => (
                <div key={t.collection} className="bg-white border border-line p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-navy font-medium">{t.collection.replace(/_/g, " ")}</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-stone2">
                      {t.published}/{t.total} published · {t.drafts} draft
                    </div>
                  </div>
                  {t.sampled?.length > 0 ? (
                    <ul className="text-xs">
                      {t.sampled.map(s => (
                        <li key={s.id} className="flex items-center justify-between border-t border-line py-1.5">
                          <span className="text-navy truncate mr-2">{s.title || s.slug}</span>
                          <button data-testid={`seo-audit-${s.id}`} onClick={() => runAudit(t.collection, s.id)}
                                  className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta">Audit →</button>
                        </li>
                      ))}
                    </ul>
                  ) : <div className="text-xs text-stone2">No published items in this collection.</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-7">
          {!selected ? (
            <div className="bg-white border border-line p-8 text-center text-sm text-stone2">
              Select any item to run its pre-publication SEO audit.
            </div>
          ) : loadingAudit || !audit ? (
            <div className="bg-white border border-line p-8 text-center text-xs text-stone2">Running audit…</div>
          ) : (
            <div className="bg-white border border-line p-6" data-testid="seo-audit-panel">
              <div className="flex items-start justify-between mb-4 gap-4">
                <div className="min-w-0">
                  <div className="mono text-[10px] uppercase tracking-widest text-prestige">{audit.collection}</div>
                  <div className="font-serif text-xl text-navy truncate">{audit.title || audit.slug}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-1">status: {audit.status || "draft"}</div>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-serif ${audit.summary.publishable ? "text-[#3F6250]" : "text-[#D97706]"}`}
                       data-testid="seo-score">{audit.summary.score_pct}%</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone2">
                    {audit.summary.pass} pass · {audit.summary.warn} warn · {audit.summary.fail} fail
                  </div>
                </div>
              </div>

              {audit.canonical && (
                <div className="text-xs text-stone2 mb-4 truncate border border-line px-3 py-2">
                  <span className="mono text-[10px] uppercase tracking-widest text-prestige mr-2">Canonical</span>{audit.canonical}
                </div>
              )}

              <ul className="space-y-2 mb-6">
                {audit.checks.map(c => (
                  <li key={c.name} data-testid={`seo-check-${c.name}`} className="flex items-start gap-3 border-b border-line py-2">
                    <div className="pt-0.5">{statusIcon[c.status]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-navy">{c.name.replace(/_/g, " ")}</div>
                      <div className="text-xs text-stone2">{c.message}</div>
                    </div>
                    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 shrink-0 ${
                      c.status === "pass" ? "bg-[#3F6250]/10 text-[#3F6250]" :
                      c.status === "warn" ? "bg-[#D97706]/15 text-[#D97706]" :
                      c.status === "fail" ? "bg-[#9F1239]/15 text-[#9F1239]" :
                      "bg-stone2/10 text-stone2"
                    }`}>{c.status}</span>
                  </li>
                ))}
              </ul>

              {audit.json_ld_sample && (
                <details data-testid="seo-jsonld" className="border border-line px-3 py-2">
                  <summary className="cursor-pointer text-xs text-navy mono uppercase tracking-widest">View generated JSON-LD</summary>
                  <pre className="mt-2 text-[10px] text-stone2 whitespace-pre-wrap font-mono overflow-x-auto">{JSON.stringify(audit.json_ld_sample, null, 2)}</pre>
                </details>
              )}

              {COLL_TO_TYPE_ROUTE[audit.collection] && (
                <div className="mt-4">
                  <Link to={`/admin/content/${COLL_TO_TYPE_ROUTE[audit.collection]}/${audit.item_id}`}
                        className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta">
                    Open editor →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
